import { Box, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { CloseIcon } from 'assets/icons/v2';

type ChipInputChipsProps = {
  values: { id: string; label: string }[];
  onRemove: (data: { id: string; label: string; e: React.MouseEvent }) => void;
  variant?: 'default' | 'outlined';
};

/**
 * ChipInputChips component - Displays chips in a wrapping layout
 * Chips will wrap naturally due to parent container's flexWrap="wrap"
 * @param variant - 'default' for green background (used in inputs), 'outlined' for white with border (used for display lists)
 */
export const ChipInputChips = ({
  values,
  onRemove,
  variant = 'default',
}: ChipInputChipsProps): ReactElement => {
  const isOutlined = variant === 'outlined';

  return (
    <>
      {values.map((value) => (
        <Box
          key={value.id}
          bg={isOutlined ? 'grey.white' : 'tag.background.green'}
          border={isOutlined ? 'default' : 'none'}
          borderColor={isOutlined ? 'border.accent' : undefined}
          h="28px"
          px={isOutlined ? '10px' : 'xs'}
          py={isOutlined ? '5px' : '3px'}
          borderRadius={isOutlined ? '5px' : 'xs'}
          display="flex"
          alignItems="center"
          gap={isOutlined ? '5px' : 'xs'}
          onClick={(e) => e.stopPropagation()}
          flexShrink={0}
        >
          <Text
            textStyle={isOutlined ? 'body.s.medium' : 'body.s.regular'}
            color={isOutlined ? 'text.secondary' : 'border.accent.active'}
            whiteSpace="nowrap"
          >
            {value.label}
          </Text>
          <Box
            as="button"
            onClick={(e: React.MouseEvent) => onRemove({ id: value.id, label: value.label, e })}
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ opacity: '70' }}
            w="16px"
            h="16px"
            flexShrink={0}
          >
            <CloseIcon
              w="16px"
              h="16px"
              color={isOutlined ? 'text.secondary' : 'border.accent.active'}
            />
          </Box>
        </Box>
      ))}
    </>
  );
};
